/**
 * be OAuth 콜백 계약 (cmong-be google-oauth.service 와 합의)
 * 성공: ?google=connected&platform_account_id=...
 * 실패: ?google=failed&reason=...
 */
export const OAUTH_STATUS_KEY = 'google';
export const OAUTH_SUCCESS_VALUE = 'connected';
export const OAUTH_PLATFORM_ACCOUNT_ID_KEY = 'platform_account_id';
export const OAUTH_MESSAGE_KEY = 'reason';

/** reason 코드는 사용자에게 그대로 노출하지 않고 문구로 바꿔 보여준다 */
export const OAUTH_FAILURE_GUIDE: Record<string, { title: string; subTitle: string }> = {
  partial_consent: {
    title: '비즈니스 프로필 관리 권한이 필요해요',
    subTitle: '구글 동의 화면에서 모든 항목에 체크한 뒤 다시 시도해주세요',
  },
  no_business_account: {
    title: '구글 비즈니스 프로필이 없는 계정이에요',
    subTitle: '비즈니스 프로필을 먼저 등록한 뒤 다시 시도해주세요',
  },
  already_registered: {
    title: '이미 등록된 플랫폼 계정입니다',
    subTitle: '다른 구글 계정으로 시도해주세요',
  },
  login_failed: {
    title: '구글 로그인에 실패했어요',
    subTitle: '비즈니스 프로필 권한이 있는 계정인지 확인해주세요',
  },
  invalid_grant: {
    title: '구글 연동 권한이 확인되지 않았어요',
    subTitle: '동의 화면에서 모든 항목에 동의한 뒤 다시 시도해주세요',
  },
  access_denied: {
    title: '구글 연동이 취소되었어요',
    subTitle: '동의 화면에서 허용을 눌러주세요',
  },
};

export const OAUTH_FAILURE_FALLBACK = {
  title: '구글 로그인에 실패했어요',
  subTitle: '잠시 후 다시 시도해주세요. 계속되면 채팅상담으로 문의해주세요',
};

/**
 * iframe(Store360/GRS) 안에서는 구글이 프레임 내 OAuth 를 403 으로 막는다.
 * 그래서 iframe 에서는 팝업(최상위 컨텍스트)으로 동의를 진행하고,
 * be 콜백이 이 relay 페이지로 팝업을 돌려보내면 relay 가 postMessage 로 부모 iframe 에 결과를 넘긴다.
 *
 * be(`google-oauth.service.ts` POPUP_CALLBACK_PATH)와 값이 같아야 한다.
 * next.config 의 trailingSlash: true 와 맞춰 끝에 `/` 를 둔다.
 */
export const OAUTH_POPUP_CALLBACK_PATH = '/auth/google/popup-callback/';

/** 팝업 → 부모 iframe postMessage 규약 */
export const OAUTH_POPUP_MESSAGE_TYPE = 'cmong:google-oauth';

export type GoogleOAuthPopupMessage = {
  type: typeof OAUTH_POPUP_MESSAGE_TYPE;
  /** 'connected' | 그 외 실패 */
  status: string;
  platformAccountId?: string;
  reason?: string;
};

export const isGoogleOAuthPopupMessage = (data: unknown): data is GoogleOAuthPopupMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === OAUTH_POPUP_MESSAGE_TYPE;

/** iframe 안에서 실행 중인지. cross-origin 부모면 window.top 접근이 던질 수 있어 try 로 감싼다 */
export const isEmbeddedInIframe = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/** 팝업 창 이름. 이 창이 우리가 띄운 OAuth 팝업인지 식별하는 데도 쓴다 */
export const OAUTH_POPUP_WINDOW_NAME = 'cmong-google-oauth';

/**
 * 지금 이 창이 우리가 띄운 OAuth 팝업인가.
 *
 * be 가 popup 모드를 모르거나(미배포) return_origin 이 허용목록에 없어 fe 로 폴백하면
 * 팝업이 relay 가 아니라 실제 플랫폼 계정 화면으로 착지한다. 그 화면이 콜백 파라미터를 보고
 * post-OAuth 흐름을 또 돌리면 부모(iframe)의 흐름과 경쟁해 서로의 연동을 깨뜨린다
 * (0매장 판정 시 platform_account 를 롤백 삭제하기 때문).
 *
 * window.name 은 cross-site 이동에서 비워졌다가 원래 사이트로 돌아오면 복원되지만,
 * 브라우저에 따라 다를 수 있어 부모 쪽에도 팝업을 닫는 백스톱을 함께 둔다.
 */
export const isGoogleOAuthPopupWindow = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.name === OAUTH_POPUP_WINDOW_NAME && !!window.opener && window.opener !== window;
  } catch {
    return false;
  }
};
